import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { isNull } from 'lodash';
import dayjs from 'dayjs';
import { Chip, DataGrid, GridRow, GridItem, Icon, MenuItem, Spacer, Typography, useSnackbar, Stack, Alert, Tooltip } from '@emburse/embark-core';
import { WarningIcon } from '@emburse/embark-icons';
import { DataGridHeader, IconMenu, MenuButton } from '../../components';
import { isDev } from '../../util/environment';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { DefaultCardholderModal } from './DefaultCardholderModal';
import ConfirmationModal from './ConfirmationModal';
import { getAccounts, getCardsByAccount, getDefaultCardholder, createSession, createConnection, deleteConnection } from '../../api/cardsGateway';
import { ConnectionStatus, CardsGatewayIssuer } from '../../interfaces/CardManagement';
import useAccountConfig from '../../hooks/useAccountConfig';
import { $t, useTranslation } from '../../hooks/useTranslation';
const isEmburseCardsAccount = (account) => {
    const issuerId = account.issuer.id;
    if (issuerId === CardsGatewayIssuer.Emburse) {
        return true;
    }
    const isAmexSim = account.name.includes('amex-sim');
    const isCapitalOneSim = account.name.includes('capital-one-sim');
    const isGringotts = issuerId === CardsGatewayIssuer.Gringotts;
    // all gringotts accounts are treated as emburse cards except for "sim" accounts
    return isGringotts && !isAmexSim && !isCapitalOneSim;
};
function CorpCardManagement() {
    const { accountConfig } = useAccountConfig();
    useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [cardProgramData, setCardProgramData] = useState([]);
    const [defaultCardholder, setDefaultCardholder] = useState(null);
    const [showCardholderModal, setShowCardholderModal] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [disconnectModalLoading, setDisconnectModalLoading] = useState(false);
    const [isLoadingCardProgramSession, setIsLoadingCardProgramSession] = useState(false);
    const [isDefaultCardholderLoaded, setIsDefaultCardholderLoaded] = useState(false);
    const [selectedCardAccount, setSelectedCardAccount] = useState(null);
    const [hasAnyAccountWarnings, sethasAnyAccountWarnings] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const handleError = useCallback((err) => {
        enqueueSnackbar(err.message, { variant: 'alert', AlertProps: { severity: 'error' } });
    }, [enqueueSnackbar]);
    const loadCardProgramData = async () => {
        setIsLoading(true);
        const accountsWithCards = await getAccounts().then(async (response) => {
            const accounts = JSON.parse(response.Data);
            return Promise.all(accounts.map(async (account) => {
                const cards = JSON.parse((await getCardsByAccount(account.id)).Data);
                return {
                    ...account,
                    cards
                };
            }));
        });
        setCardProgramData(accountsWithCards);
        setIsLoading(false);
    };
    useEffect(() => {
        loadCardProgramData();
        getDefaultCardholder().then((response) => {
            if (!response.Data) {
                return;
            }
            const cardholder = JSON.parse(response.Data);
            if (cardholder) {
                setDefaultCardholder(cardholder);
            }
            setIsDefaultCardholderLoaded(true);
        });
    }, []);
    const hasAccountWarning = (account) => {
        const expiration = account.connection.configuration?.oauth?.expiresAt;
        if (!expiration) {
            return false;
        }
        const expirationDay = dayjs(expiration).startOf('day');
        return dayjs().startOf('day').diff(expirationDay, 'day') >= -7;
    };
    useEffect(() => {
        // set value of hasAnyAccountWarnings
        sethasAnyAccountWarnings(cardProgramData.some((account) => hasAccountWarning(account)));
    }, [cardProgramData]);
    const addCardProgram = async (item) => {
        const issuer = {
            id: item.value,
            name: item.label
        };
        if (issuer.id === CardsGatewayIssuer.Emburse && !defaultCardholder) {
            setShowCardholderModal(true);
        }
        else {
            setIsLoadingCardProgramSession(true);
            let connection;
            try {
                connection = await createConnection(issuer);
            }
            catch (err) {
                handleError(err);
                return;
            }
            const session = JSON.parse((await createSession(connection?.id, issuer)).Data);
            window.location.href = session.startPageUrl;
            setIsLoadingCardProgramSession(false);
        }
    };
    const selectedCardAccountMenuItems = () => {
        if (isNull(selectedCardAccount)) {
            return _jsx("span", {});
        }
        const { status } = selectedCardAccount.connection;
        const showManage = isEmburseCardsAccount(selectedCardAccount);
        const showReimport = accountConfig.UIFlags.CanUseInternalTools && status === ConnectionStatus.active;
        const showReauthorize = status === ConnectionStatus.disconnected;
        const showRefresh = !showReauthorize &&
            (selectedCardAccount.issuer.id === CardsGatewayIssuer.Amex ||
                selectedCardAccount.issuer.id === CardsGatewayIssuer.CapitalOne ||
                (selectedCardAccount.issuer.id === CardsGatewayIssuer.Gringotts &&
                    (selectedCardAccount.name.toLowerCase().includes('amex-sim') ||
                        selectedCardAccount.name.toLowerCase().includes('capital-one-sim'))));
        const showDisconnect = status !== ConnectionStatus.deactivated;
        return (_jsxs(_Fragment, { children: [showManage && (_jsx(MenuItem, { onClick: () => {
                        window.open('/SSOEmburseCards.aspx', '_blank');
                    }, children: $t('Manage Program') })), _jsx(MenuItem, { onClick: () => {
                        window.location.href = `/CorpCardMapping.aspx?accountId=${selectedCardAccount.id}`;
                    }, children: $t('Edit Mapping') }), showReimport && (_jsx(MenuItem, { onClick: () => {
                        window.location.href = `/GetCardTransactions.aspx?aid=${selectedCardAccount.id}`;
                    }, children: $t('Reimport Transactions') })), (showReauthorize || showRefresh) && (_jsx(MenuItem, { onClick: async () => {
                        const session = JSON.parse((await createSession(selectedCardAccount.connection.id, selectedCardAccount.issuer)).Data);
                        window.location.href = session.startPageUrl;
                        setSelectedCardAccount(null);
                    }, children: $t(showReauthorize ? 'Re-Authorize' : 'Refresh') })), showDisconnect && (_jsx(MenuItem, { onClick: () => {
                        // modal are you sure you want to do that?
                        setShowDisconnectModal(true);
                    }, children: _jsx(Typography, { color: "error", children: $t('Delete Connection') }) }))] }));
    };
    const getAddCardProgramMenuItems = () => {
        const showDevIssuers = isDev || accountConfig.UIFlags.EnableCardsGatewayDevAccounts;
        const issuers = [
            {
                id: 'EmburseCards',
                label: 'Emburse Cards',
                value: CardsGatewayIssuer.Emburse,
                children: null
            },
            {
                id: 'AMEX',
                label: 'AMEX',
                value: CardsGatewayIssuer.Amex,
                children: null
            },
            {
                id: 'CapitalOne',
                label: 'Capital One',
                value: CardsGatewayIssuer.CapitalOne,
                children: null
            }
        ];
        if (showDevIssuers) {
            issuers.push({
                id: 'Gringotts',
                label: 'Gringotts',
                value: CardsGatewayIssuer.Gringotts,
                children: null
            });
        }
        return issuers;
    };
    const renderMultiAccountDisconnectWarning = () => (_jsxs(_Fragment, { children: [_jsx(Spacer, { direction: "vertical", size: 20 }), _jsx(Stack, { direction: "row", spacing: 1, alignItems: "flex-start", style: { width: '100%' }, children: _jsx(Alert, { severity: "warning", alertTitle: $t('Removing this connection will disconnect multiple card programs.'), style: { width: '100%' }, children: _jsxs(Stack, { direction: "column", alignItems: "flex-start", style: { minWidth: '100%' }, children: [_jsx(Typography, { variant: "body2", children: $t(`This action will impact ${selectedCardAccount.connection.accounts.length} card programs
                            and ${selectedCardAccount.cards.length} cards.`) }), _jsx(Spacer, { direction: "vertical", size: 10 }), _jsx("ul", { style: {
                                    maxHeight: '200px',
                                    overflow: 'hidden auto',
                                    paddingLeft: '15px',
                                    width: '95%'
                                }, children: selectedCardAccount.connection.accounts.map((account) => (_jsx("li", { children: account.name }, `${account.externalID}`))) })] }) }) }), _jsx(Spacer, { direction: "vertical", size: 5 })] }));
    // returns array of gridcoldef objects
    const getColumns = () => {
        const columns = [];
        // we only need to show the warning icon if any of the accounts have a warning
        // otherwise it just takes up ~20px of blank space
        if (hasAnyAccountWarnings) {
            columns.push({
                field: 'warning',
                headerName: '',
                maxWidth: 20,
                renderCell: (cell) => {
                    const account = cell.row;
                    if (hasAccountWarning(account)) {
                        return (_jsx(Tooltip, { title: $t(`
                                    Authentication token expires on ${dayjs(account.connection.configuration.oauth.expiresAt).format('MM/DD/YYYY')}. 
                                    Select Refresh to prevent interruption of transaction import.
                                `), placement: "bottom", children: _jsx(Icon, { iconUrl: WarningIcon }) }));
                    }
                    return _jsx("div", {});
                }
            });
        }
        return columns.concat([
            {
                field: 'issuer',
                headerName: $t('Issuer'),
                width: 300,
                renderCell: (cell) => {
                    const { name } = cell.value;
                    return _jsx(Typography, { children: name });
                }
            },
            {
                field: 'name',
                headerName: $t('Program Name'),
                minWidth: 250,
                flex: 1,
                renderCell: (cell) => _jsx(Typography, { children: cell.value })
            },
            {
                field: 'accounts',
                headerName: $t('Cards'),
                minWidth: 100,
                flex: 0.5,
                renderCell: (cell) => {
                    const { cards } = cell.row;
                    return _jsx(Typography, { children: cards?.length || '' });
                }
            },
            {
                field: 'status',
                headerName: $t('Status'),
                width: 120,
                flex: 0.5,
                renderCell: (cell) => {
                    const { status } = cell.row.connection;
                    const color = (() => {
                        if (status === ConnectionStatus.active) {
                            return 'success';
                        }
                        return 'warning';
                    })();
                    return (_jsx(Chip, { size: "small", color: color, variant: "outlined", style: {
                            textTransform: 'capitalize'
                        }, label: status }));
                }
            },
            {
                field: 'action',
                renderHeader: () => _jsx("span", {}),
                renderCell: (cell) => {
                    const row = cell.row;
                    return (_jsx("div", { style: {
                            width: '100%',
                            textAlign: 'right'
                        }, children: _jsx(IconMenu, { anchorHorizontalPosition: "left", onOpen: () => {
                                setSelectedCardAccount(row);
                            }, children: selectedCardAccountMenuItems() }) }));
                }
            }
        ]);
    };
    return (_jsxs(_Fragment, { children: [_jsxs(SingleColumnLayout, { children: [_jsxs(GridRow, { justifyContent: "space-between", direction: "row", children: [_jsx(GridItem, { children: _jsx(DataGridHeader, { title: $t('Card Programs') }) }), _jsx(GridItem, { children: _jsx(MenuButton, { size: "medium", menuItems: getAddCardProgramMenuItems(), loading: isLoadingCardProgramSession, onMenuSelect: (item) => addCardProgram(item), disabled: !isDefaultCardholderLoaded, children: $t('Add a Card Program') }) })] }), _jsx(Spacer, { size: 42 }), _jsx(GridRow, { children: _jsx(DataGrid, { pageSizeOptions: [10, 20, 50], loading: isLoading, autoHeight: true, localeText: {
                                noRowsLabel: $t("Add a Card Program to automatically import card transactions to your users' Wallets.")
                            }, columns: getColumns(), rows: cardProgramData }) })] }), _jsx(DefaultCardholderModal, { show: showCardholderModal, onClose: () => setShowCardholderModal(false), defaultCardholder: defaultCardholder, onUpdateDefaultCardholder: (newDefaultCardholder) => {
                    setDefaultCardholder(newDefaultCardholder);
                    setShowCardholderModal(false);
                } }), _jsx(ConfirmationModal, { show: showDisconnectModal, onClose: () => {
                    setShowDisconnectModal(false);
                    setSelectedCardAccount(null);
                }, loading: disconnectModalLoading, title: $t('Delete Connection'), confirmText: $t('Delete Connection'), isDanger: true, onConfirm: async () => {
                    setDisconnectModalLoading(true);
                    await new Promise((resolve) => {
                        window.setTimeout(() => {
                            resolve(true);
                        }, 3000);
                    });
                    await deleteConnection(accountConfig.Emp.Company.UID, selectedCardAccount.connection.id);
                    setDisconnectModalLoading(false);
                    setShowDisconnectModal(false);
                    setSelectedCardAccount(null);
                    // reload card program data to remove the deactivated record
                    loadCardProgramData();
                }, children: _jsxs(_Fragment, { children: [_jsx(Typography, { children: $t(`Deleting the connection to this card program will stop all future transactions associated with this card program from importing
                        into your users' wallets. If you reconnect the card program at a later date, transaction ids will not match and may result in
                        duplicate expense imports. This action cannot be reversed.`) }), _jsx("br", {}), _jsx(Typography, { children: $t('Are you sure you want to delete the connection to this card program?') }), 
                        // (CER-14593) Display warning message when action impacts multiple card programs (non-emburse)
                        selectedCardAccount &&
                            selectedCardAccount.connection.accounts.length > 1 &&
                            renderMultiAccountDisconnectWarning()] }) })] }));
}
export default CorpCardManagement;
